import { html, TemplateResult, CSSResultGroup, SVGTemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import componentStyles from './idb-cta-banner.css.lit';
import { SlotController } from '../../controllers/slot-controller';
import '../../base/outline-container/outline-container';
import { bannerBackgroundPropertyData } from '../../../data';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeSVG } from 'lit-html/directives/unsafe-svg.js';
import { iconMap } from '../idb-icon-card/idb-icon-card';

/**
 * The CTA Banner component.
 * @element idb-cta-banner
 * @property title-weight - Sets the title bold etc.
 * @property banner-background - Sets background color.
 * @slot background - Background image
 * @slot content - Main content
 * @slot cta - Call to actions
 */

export type FontWeights = 'normal' | 'semibold' | 'bold';
export type BannerBackgrounds = typeof bannerBackgroundPropertyData;

@customElement('idb-cta-banner')
export class IdbCtaBanner extends OutlineElement {
  slots = new SlotController(
    this, // This, the host element.
    true // To shift or not to shift LightDom nodes to ShadowDOM.
  );
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Let's you set the title weight.
   */
  @property({ attribute: 'title-weight' })
  fontWeight: FontWeights;

  /**
   * The background of the banner.
   */
  @property({ type: String, attribute: 'banner-background' })
  bannerBackground: BannerBackgrounds;

  /**
   * The icon to display in the card.
   */
  @property({ type: String, attribute: 'icon' })
  icon: keyof typeof iconMap;

  /**
   * Parameter to remove the container.
   */
  @property({ type: Boolean, reflect: true, attribute: 'remove-container' })
  removeContainer = false;

  @property({ type: Boolean, attribute: 'less-spacing' })
  lessSpacing = false;

  renderContent() {
    const bannerClasses = {
      wrapper: true,
      [`banner-background--${this.bannerBackground}`]:
        this.bannerBackground !== undefined,
      [`banner-cta-with-icons`]: this.icon,
    };

    return html`
      <div class="${classMap(bannerClasses)}">
      ${
        !this.icon
          ? html`<div class="image-slot-wrapper">
                <slot name="background"></slot>
              </div>`
          : ''
      }
      <div class="content">
        ${
          this.icon
            ? html`<div class="icon" aria-hidden="true">
                    ${unsafeSVG(iconMap[this.icon]) as SVGTemplateResult}
                  </div>`
            : ''
        }
        <idb-heading
          level="h2"
          level-style="${this.fontWeight}"
          level-size="xl"
        >
          <slot name="content"></slot>
        </idb-heading>
        <slot name="cta"></slot>
      </div>
    </div>
    `;
  }

  render(): TemplateResult {
    return html`
      ${
        this.removeContainer
          ? this.renderContent()
          : html`
          <outline-container x-padding>
            ${this.renderContent()}
          </outline-container>
          `
      }
    `;
  }
}
